/**
 * The four fields required at intake, as the confirm-new-deal step asks for them.
 *
 * Create Deal on an intake record is a person's act, so the server refuses it
 * (422 `required_fields_missing`) until the loan purpose, the city and state,
 * the initial amount and the fully funded amount are all there, naming each
 * that is not. The dialog turns that refusal into one input per missing field and
 * sends the answers back as the convert's `overrides`. The mail pipeline never
 * comes through here: a package that states less is still created, flagged.
 */
import { parseMoneyText } from '@/services/dealFile'
import type { ConvertPayload } from '@/services/ingestion'

export type RequiredGap = { field: string; label: string; message: string }

/** The answers as the convert's `overrides` take them. */
export type ConfirmOverrides = NonNullable<ConvertPayload['overrides']>

const AMOUNT_FIELDS = new Set(['loan_amount', 'fully_funded_amount'])

/** The server's refusal as the list of fields it names, or null for any other error. */
export function requiredGapsFromRefusal(detail: unknown): RequiredGap[] | null {
  if (!detail || typeof detail !== 'object') return null
  const body = detail as { code?: unknown; fields?: unknown }
  if (body.code !== 'required_fields_missing' || !Array.isArray(body.fields)) return null
  const gaps: RequiredGap[] = []
  for (const entry of body.fields) {
    if (!entry || typeof entry !== 'object') continue
    const { field, label, message } = entry as Record<string, unknown>
    if (typeof field !== 'string') continue
    gaps.push({
      field,
      label: typeof label === 'string' && label ? label : field,
      message: typeof message === 'string' && message ? message : 'Required',
    })
  }
  return gaps.length ? gaps : null
}

/**
 * What the person typed, as the `overrides` the convert takes — amounts as
 * dollars, everything else as text — and a message for each answer that is
 * missing or not an amount. `overrides` is empty of anything that has an error.
 */
export function overridesFromAnswers(
  gaps: ReadonlyArray<RequiredGap>,
  answers: Record<string, string>,
): { overrides: ConfirmOverrides; errors: Record<string, string> } {
  const overrides: Record<string, string | number> = {}
  const errors: Record<string, string> = {}
  for (const gap of gaps) {
    const raw = (answers[gap.field] ?? '').trim()
    if (!raw) {
      errors[gap.field] = 'Required'
      continue
    }
    if (AMOUNT_FIELDS.has(gap.field)) {
      const dollars = parseMoneyText(raw)
      if (dollars === null || !(dollars > 0)) errors[gap.field] = 'Enter an amount'
      else overrides[gap.field] = dollars
    } else {
      overrides[gap.field] = raw
    }
  }
  return { overrides: overrides as ConfirmOverrides, errors }
}
